import { Box, Grid, GridItem, Heading, Text, Stack, HStack, Button, Avatar, Container, Tabs, TabList, Tab, TabPanels, TabPanel, SimpleGrid, Image, ButtonGroup, useDisclosure, IconButton } from "@chakra-ui/react";
import { ReactElement, useEffect, useState } from "react";
import VideoPlayer from "components/video-player";
import Layout from "scenes/layouts";
import ProgramService from "../../../services/programs/program-service";
import { useNavigate, useParams } from "react-router-dom";
import ProgramPricing from "./program-pricing";
import ProgramStatistic from "./program-statistic";
import ProgramListLesson from "./program-list-lesson";
import LogosNotification from "../../../utils/notification-utils";
import AddLessonGroupDialog from "../lesson-groups/add-dialog";
import { BsCameraVideo, BsImage, BsPencil, BsXCircle } from "react-icons/bs";
import EditImageDialog from "../../../components/edit-image-dialog";
import UploadVideoDialog from "../../../components/upload-video-dialog";
import ConfirmDialog from "../../../components/confirm-dialog";
import { FaBug, FaHourglassHalf } from "react-icons/fa";
import LoadingScreen from "../../../components/loading-screen";
import DuplicateProgramDialog from "./duplicate-dialog";

const initialProgramValue = {
    name: '',
    description: '',
    isSubscribed: false,
    isCompleted: false,
    totalLesson: 0,
    totalCompletedLesson: 0,
    totalDuration: '',
    overview: '',
    enrolled: 0,
    posterUrl: '',
    price: 0,
    priceOriginal: 0,
    previewVideoUrl: '',
    author: { name: '', imageUrl: '', shortBioData: '', about: '' },
    lessonGroups: []
};

const ProgramDetailScene = (): ReactElement => {
    const navigate = useNavigate();
    const [isLoading, setLoading] = useState<boolean>(true);
    const [program, setProgram] = useState<any>(initialProgramValue);
    const [videoUploadQueue, setVideoUploadQueue] = useState<any>(null);
    const { programId }: any = useParams();
    const [tabIndex, setTabIndex] = useState<number>(0);
    const { isOpen: isShowAddLessonGroupDialog, onOpen: onShowAddLessonGroupDialog, onClose: onCloseAddLessonGroupDialog } = useDisclosure();
    const { isOpen: isShowChangePosterDialog, onOpen: onShowChangePosterDialog, onClose: onCloseChangePosterDialog } = useDisclosure();
    const { isOpen: isShowUploadVideoDialog, onOpen: onShowUploadVideoDialog, onClose: onCloseUploadVideoDialog } = useDisclosure();
    const { isOpen: isShowDeleteVideoDialog, onOpen: onShowDeleteVideoDialog, onClose: onCloseDeleteVideoDialog } = useDisclosure();
    const [isDeletingVideo, setIsDeletingVideo] = useState<boolean>(false);
    const { isOpen: isShowDuplicateDialog, onOpen: onShowDuplicateDialog, onClose: onCloseDuplicateDialog } = useDisclosure();

    useEffect(() => {
        setLoading(true);
        ProgramService.GetProgramAdmin(programId).then((response: any) => {
            setProgram(response.result);

            ProgramService.GetProgramPreviewVideoUploadQueue(programId).then((response: any) => {
                setVideoUploadQueue(response.result);
            }).finally(() => {
                setLoading(false);
            });
        });
    }, []);

    useEffect(() => {
        setTabIndex(tabIndex);
    }, [program]);

    const handleActivate = () => {
        ProgramService.Activate(program.id).then((res: any) => {
            LogosNotification.Success("Program berhasil di Aktifkan");
            setTimeout(() => { window.location.reload(); }, 2000);
        });
    };

    const handleDeActivate = () => {
        ProgramService.DeActivate(program.id).then((res: any) => {
            LogosNotification.Success("Program berhasil di Non-Aktifkan");
            setTimeout(() => { window.location.reload(); }, 2000);
        });
    };

    const handleReUpdateProgramSubscription = () => {
        ProgramService.ReUpdateProgramSubscription(program.id).then((res: any) => {
            LogosNotification.Success("Program Subscription berhasil diperbaharui");
            setTimeout(() => { window.location.reload(); }, 2000);
        });
    };

    const handleRefreshData = (program: any) => {
        setProgram(program);
    };

    const handleChangePoster = (imageData: any, finallyFunction: any) => {
        ProgramService.ChangePosterImage(programId, imageData).then((res: any) => {
            onCloseChangePosterDialog();
            setProgram(res.result);
            LogosNotification.Success("Poster telah berhasil diperbaharui.");
        }).finally(() => {
            finallyFunction();
        });
    };

    const handleUploadVideoChunk = (chunk: any, counter: number, filename: string) => {
        return ProgramService.UploadVideoProgramPreviewChunk(programId, chunk, counter, filename);
    };

    const handleUploadVideoComplete = (chunkId: string, fileName: string, fileSize: number) => {
        return ProgramService.UploadVideoProgramPreviewComplete(programId, chunkId, fileName, fileSize);
    };

    const handleDeleteVideo = () => {
        ProgramService.DeleteVideoProgramPreview(programId).then((res: any) => {
            //setProgram(res.result);
            onCloseDeleteVideoDialog();
            LogosNotification.Success("Video Preview berhasil dihapus.");
            window.location.reload();
        });
    };

    if (isLoading)
        return <LoadingScreen />;

    return (
        <Layout>
            <Box backgroundColor={"gray.700"} >
                <Container maxWidth={"6xl"} pt={3}>
                    <Box as="main" mt={10}>
                        <Box bgSize={'36em'} color={'whiteAlpha.900'}>
                            <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)' }} gap={5}>
                                <GridItem>
                                    <Box>
                                        <Heading fontSize={'4xl'} fontWeight={'extrabold'}>{program.name}</Heading>
                                        <Text fontSize={'md'} fontWeight={"bold"}>
                                            {program.author.name}
                                        </Text>
                                        <Text fontSize={'sm'} mt={5}>
                                            {program.description}
                                        </Text>
                                        <Stack mt={6} direction={'row'} spacing={4} align={'center'}>
                                            <Avatar size={"lg"} src={program.author.imageUrl} />
                                            <Stack direction={'column'} spacing={0} fontSize={'sm'}>
                                                <Text fontWeight={600}>{program.author.name}</Text>
                                                <Text color={'gray.500'}>{program.author.shortBioData}</Text>
                                            </Stack>
                                        </Stack>
                                        <Button
                                            isFullWidth mt={10}
                                            onClick={() => navigate(`/programs/${programId}/edit`)}
                                            mb={2}
                                        >
                                            Edit Program
                                        </Button>
                                    </Box>
                                </GridItem>
                                <GridItem colSpan={2}>
                                    <Box rounded={10} pl={{ base: 0, md: 0 }}>
                                        {
                                            program.previewVideoUrl &&
                                            <VideoPlayer options={{
                                                autoplay: false,
                                                muted: false,
                                                poster: program.posterUrl,
                                                sources: [
                                                    {
                                                        src: program.previewVideoUrl,
                                                        type: "application/x-mpegURL"
                                                    }
                                                ]
                                            }} />
                                        }
                                        {
                                            !program.previewVideoUrl &&
                                            <Image src={program.posterUrl} rounded={'xl'} />
                                        }
                                        <IconButton
                                            variant={"solid"}
                                            colorScheme={'whiteAlpha'}
                                            isRound={true}
                                            position={'relative'} bottom={70} ml={1} mb={1}
                                            aria-label='Ubah Poster Program'
                                            icon={<BsImage />}
                                            style={{ zIndex: 1 }}
                                            onClick={onShowChangePosterDialog}
                                        />
                                        {
                                            isShowChangePosterDialog &&
                                            <EditImageDialog onClose={onCloseChangePosterDialog} title="Ubah Poster Program" onSubmit={handleChangePoster} aspectRatio={16 / 9} helperText={"Ukuran: 1280 x 720 px"} />
                                        }

                                        {
                                            !(videoUploadQueue && (videoUploadQueue.status == 0 || videoUploadQueue.status == 10)) && !program.previewVideoUrl &&
                                            <IconButton
                                                variant={"solid"}
                                                colorScheme={'blackAlpha'}
                                                isRound={true}
                                                position={'relative'} bottom={70} float={'right'} mr={1} mb={1}
                                                aria-label='Upload Video'
                                                icon={<BsCameraVideo />}
                                                style={{ zIndex: 1 }}
                                                onClick={onShowUploadVideoDialog}
                                            />
                                        }
                                        {
                                            isShowUploadVideoDialog &&
                                            <UploadVideoDialog onClose={onCloseUploadVideoDialog} title="Upload Video Lesson" handleUploadChunk={handleUploadVideoChunk} handleUploadComplete={handleUploadVideoComplete} />
                                        }

                                        {
                                            program.previewVideoUrl &&
                                            <IconButton
                                                variant={"solid"}
                                                colorScheme={'blackAlpha'}
                                                isRound={true}
                                                position={'relative'} bottom={70} float={'right'} mr={1} mb={1}
                                                aria-label='Hapus Video'
                                                icon={<BsXCircle />}
                                                style={{ zIndex: 1 }}
                                                onClick={onShowDeleteVideoDialog}
                                            />
                                        }
                                        {
                                            isShowDeleteVideoDialog &&
                                            <ConfirmDialog
                                                title={'Hapus Video'}
                                                message={`Apakah Anda yakin untuk menghapuskan video ini?`}
                                                colorScheme={'red'}
                                                buttonLabel={'Hapuskan'}
                                                onSubmit={handleDeleteVideo}
                                                onClose={onCloseDeleteVideoDialog}
                                                isLoading={isDeletingVideo}
                                            />
                                        }
                                        {
                                            videoUploadQueue && (videoUploadQueue.status == 0 || videoUploadQueue.status == 10) &&
                                            <IconButton
                                                variant={"solid"}
                                                colorScheme={'blackAlpha'}
                                                isRound={true}
                                                position={'relative'} bottom={70} float={'right'} mr={1} mb={1}
                                                aria-label='In Progress Video'
                                                icon={<FaHourglassHalf />}
                                                style={{ zIndex: 1 }}
                                                onClick={() => { alert("Sedang proses Konversi ke File Streaming. Prosesnya memang lumayan lama, Nikmati saja Kopi Susu terlebih dahulu.") }}
                                            />
                                        }
                                        {
                                            videoUploadQueue && videoUploadQueue.status == 2 &&
                                            <IconButton
                                                variant={"solid"}
                                                colorScheme={'red'}
                                                isRound={true}
                                                position={'relative'} bottom={70} float={'right'} mr={1} mb={1}
                                                aria-label='Error Upload Video'
                                                icon={<FaBug />}
                                                style={{ zIndex: 1 }}
                                                onClick={() => { alert(videoUploadQueue.message) }}
                                            />
                                        }
                                    </Box>
                                </GridItem>
                            </Grid>
                        </Box>

                        <Box mt={0}>
                            <Tabs isFitted defaultIndex={tabIndex} index={tabIndex} onChange={(index) => setTabIndex(index)}>
                                <TabList>
                                    <Tab>Overview</Tab>
                                    <Tab>Lessons</Tab>
                                </TabList>

                                <TabPanels>
                                    <TabPanel px={0}>
                                        <ProgramStatistic program={program} />

                                        <Box bgColor={'whiteAlpha.900'} my={4} p={10}>
                                            <Box className='logos-quill' dangerouslySetInnerHTML={{ __html: program.overview }}></Box>
                                            <Heading
                                                fontSize={{ base: 'xl', lg: '3xl' }}
                                                textAlign={"center"}
                                                fontWeight={'extrabold'}
                                                mt={10}
                                                mb={4}
                                            >
                                                {program.author.name}, {program.author.shortBioData}
                                            </Heading>

                                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 5, lg: 8 }}>
                                                <Box flex={1}>
                                                    <Text px={{ base: 10, xs: 0 }} fontSize={"md"} className='logos-quill' dangerouslySetInnerHTML={{ __html: program.author.about }}>
                                                    </Text>
                                                </Box>
                                                <Box flex={1}>
                                                    <Image
                                                        src={program.author.imageUrl}
                                                        alt={program.author.name}
                                                        objectFit='cover'
                                                        rounded={'lg'}
                                                    />
                                                </Box>
                                            </SimpleGrid>

                                            <ProgramPricing program={program} onShowCheckoutPage={() => { }} />

                                        </Box>

                                        <Box my={2} >
                                            <ButtonGroup mb={2}>
                                                {
                                                    program.status == 10 &&
                                                    <Button variant={"solid"} colorScheme={'green'} onClick={handleActivate}>
                                                        Activate
                                                    </Button>
                                                }

                                                {
                                                    program.status == 20 &&
                                                    <Button variant={"solid"} colorScheme={'red'} onClick={handleDeActivate}>
                                                        DeActivate
                                                    </Button>
                                                }


                                                <Button variant={"solid"} onClick={onShowDuplicateDialog}>
                                                    Duplicate
                                                </Button>

                                                {
                                                    isShowDuplicateDialog &&
                                                    <DuplicateProgramDialog
                                                        program={program}
                                                        onClose={onCloseDuplicateDialog}
                                                    />
                                                }
                                                {/*<Button variant={"solid"} colorScheme={'blue'} onClick={handleReUpdateProgramSubscription}>*/}
                                                {/*    Update Subscription*/}
                                                {/*</Button>*/}
                                            </ButtonGroup>
                                        </Box>
                                    </TabPanel>

                                    <TabPanel px={0}>
                                        <ProgramListLesson program={program} onRefreshData={handleRefreshData} />
                                        <Box my={2} >
                                            <ButtonGroup mb={2}>
                                                <Button onClick={onShowAddLessonGroupDialog}>
                                                    Buat Group Baru
                                                </Button>
                                            </ButtonGroup>
                                            {
                                                isShowAddLessonGroupDialog &&
                                                <AddLessonGroupDialog
                                                    program={program}
                                                    onClose={onCloseAddLessonGroupDialog}
                                                    onRefreshData={(program: any) => {
                                                        onCloseAddLessonGroupDialog();
                                                        handleRefreshData(program);
                                                    }}
                                                />
                                            }
                                        </Box>
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        </Box>
                    </Box>

                </Container>
            </Box>
        </Layout>
    );
};

export default ProgramDetailScene;